"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const categories = [
{ id: "all", label: "All Collections" },
{ id: "music", label: "Music" },
{ id: "sports", label: "Sports" },
{ id: "art", label: "Art & Photography" },
{ id: "entertainment", label: "Entertainment" }];


const useCases = [
{
  id: "uc-1",
  category: "music",
  title: "1st Press Dark Side of the Moon",
  collector: "Marcus Chen · 187 items",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_131a10067-1771515383025.png",
  imageAlt: "Original 1973 Pink Floyd vinyl record with dramatic single-source lighting showing label detail",
  span: "col-span-1 row-span-2",
  height: "h-80"
},
{
  id: "uc-2",
  category: "sports",
  title: "Jordan\'s 1991 Finals Jersey",
  collector: "David Kim · 312 items",
  image: "https://images.unsplash.com/photo-1685279473488-497e2d3ddef0",
  imageAlt: "Signed basketball jersey in museum display case with authentication documentation",
  span: "col-span-1",
  height: "h-56"
},
{
  id: "uc-3",
  category: "art",
  title: "Banksy Signed Print #12/50",
  collector: "Sofia Andersson · 64 items",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1004f013f-1767608935341.png",
  imageAlt: "Limited edition art print with artist signature visible under dramatic gallery lighting",
  span: "col-span-1",
  height: "h-56"
},
{
  id: "uc-4",
  category: "entertainment",
  title: "Original Star Wars Prop",
  collector: "James Okafor · 89 items",
  image: "https://images.unsplash.com/photo-1689797610417-f2cf2793ddd4",
  imageAlt: "Movie prop displayed on dark background with behind-the-scenes photo overlay",
  span: "col-span-1",
  height: "h-48"
},
{
  id: "uc-5",
  category: "music",
  title: "Hendrix Signed Stratocaster",
  collector: "Elena Vasquez · 23 items",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d1247daa-1771515385267.png",
  imageAlt: "Vintage electric guitar with visible signature under macro photography lighting",
  span: "col-span-1",
  height: "h-48"
},
{
  id: "uc-6",
  category: "sports",
  title: "Pelé Match-Used Ball 1970",
  collector: "Roberto Ferreira · 156 items",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ba52f78e-1771515388934.png",
  imageAlt: "World Cup match-used football displayed with certification documents and historical photographs",
  span: "col-span-1",
  height: "h-56"
}];


export default function UseCaseGallery() {
  const [activeCategory, setActiveCategory] = useState("all");
  const sectionRef = useRef<HTMLDivElement>(null);

  const filtered = activeCategory === "all" ?
  useCases :
  useCases?.filter((u) => u?.category === activeCategory);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal-hidden").forEach((el, i) => {
              setTimeout(() => el.classList.add("revealed"), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 md:py-32 px-6 md:px-8 bg-bg-primary"
      aria-label="Use case gallery">
      
      <div className="max-w-container mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 reveal-hidden">
          <div>
            <span className="font-mono text-[11px] text-accent-crimson uppercase tracking-[0.3em] block mb-4">
              Built for Collectors Like You
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[0.92] tracking-tight text-white">
              Every collection.
              <br />
              <span className="italic text-gray-400">Every passion.</span>
            </h2>
          </div>
          <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
            From vinyl to vintage cars — TapTale adapts to the unique language of your collection.
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-10 reveal-hidden stagger-1">
          {categories?.map((cat) =>
          <button
            key={cat?.id}
            onClick={() => setActiveCategory(cat?.id)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
            activeCategory === cat?.id ?
            "bg-accent-crimson text-white" : "border border-white/10 text-gray-500 hover:border-white/25 hover:text-white"}`
            }>
            
              {cat?.label}
            </button>
          )}
        </div>

        {/* Masonry Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered?.map((item, i) =>
          <div
            key={item?.id}
            className={`reveal-hidden stagger-${Math.min(i + 1, 5)} relative rounded-2xl overflow-hidden img-zoom group cursor-pointer card-offset-shadow`}>
            
              <div className={`${item?.height} w-full`}>
                <AppImage
                src={item?.image}
                alt={item?.imageAlt}
                fill
                className="object-cover" />
              
              </div>
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" />

              {/* NFC Badge */}
              <div className="absolute top-4 right-4 glass-card-dark rounded-full px-2.5 py-1 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="nfc-pulse w-1.5 h-1.5 rounded-full bg-accent-crimson" />
                <span className="font-mono text-[9px] text-white uppercase tracking-wider">NFC</span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-white text-sm font-medium leading-snug mb-1">
                  {item?.title}
                </p>
                <p className="text-gray-500 text-xs flex items-center gap-1.5">
                  <Icon name="UserCircleIcon" size={12} />
                  {item?.collector}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}